<!-- @format -->
<template>
    <div class="mdf-pay-funds-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-show="isLoading" />
                <div v-show="!isLoading" class="widget-container">
                    <pay-funds-details-step
                        v-if="currentStep === steps.payFundStep"
                        ref="payFundsStep"
                        :mdf-request-main-details="mdfRequestMainDetails"
                        :request-details="requestDetails"
                        :partner-name="partnerName"
                        @next-step="goToStep('makePaymentStep')"
                    />
                    <make-payment-funds-step
                        v-if="currentStep === steps.makePaymentStep"
                        ref="makePaymentStep"
                        :payment-data="paymentDetails"
                        :invoice-table-details="invoiceTableDetails"
                        :account-id="agencyAccountId"
                        @next-step="goToStep('successStep')"
                    />
                    <mdf-pay-funds-success-step
                        v-if="currentStep === steps.successStep"
                        ref="successStep"
                        :invoice-table-details="invoiceTableDetails"
                        :payment-details="paymentDetails"
                        :invoice-id="invoiceId"
                        @update:payment-details="updateDetails"
                        @next-step="closeWidget"
                        @view-invoice="handleViewInvoice"
                        @update:loading="updateIsLoading"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        v-show="currentStep.hasCancelButton"
                        class="transparent-btn"
                        @click="closeWidget"
                    >
                        Cancel
                    </app-button>
                </div>
                <div
                    v-if="currentStep === steps.payFundStep"
                    v-tooltip="!isFundsPayer ? tooltipOptions : null"
                    class="action-container"
                >
                    <app-button
                        :disabled="!isFundsPayer || isLoading"
                        @click="currentStep.actionButton"
                        >Pay Funds</app-button
                    >
                </div>
                <div
                    v-else-if="currentStep === steps.makePaymentStep"
                    class="action-container make-payment"
                >
                    <app-button class="secondary-btn" @click="goToStep('payFundStep')">
                        <span class="fa fa-arrow-left"></span>
                        Go Back
                    </app-button>
                    <app-button
                        class="save-btn"
                        :loading="isPaying"
                        :disabled="
                            !paymentDetails.clientHasBankAccount &&
                            paymentDetails.selectedPaymentOption === 'payInGlasshive'
                        "
                        @click="currentStep.actionButton"
                        >Make Payment</app-button
                    >
                </div>
                <div v-if="currentStep === steps.successStep" class="action-container">
                    <app-button :disabled="isLoading" @click="currentStep.actionButton"
                        >Done</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const PayFundsDetailsStep = () =>
    import('@/components/sliding-widgets/mdf-pay-funds-widget/pay-funds-details-step');
const MakePaymentFundsStep = () =>
    import('@/components/sliding-widgets/mdf-pay-funds-widget/make-payment-funds-step');
const MdfPayFundsSuccessStep = () =>
    import('@/components/sliding-widgets/mdf-pay-funds-widget/mdf-pay-funds-success-step');

import { MDFAgencyRequestMixin } from '@/mixins/mdf-agency-request-mixin';
import { MDFPayFundsMixin } from '@/mixins/mdf-pay-funds-mixin';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import { MDFInvoicesService } from '@/scripts/services/invoices/mdf-invoices-service.js';
import { FeeEnumMixin } from '@/mixins/fee-enum-mixin';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'mdf-pay-funds-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'pay-funds-details-step': PayFundsDetailsStep,
        'make-payment-funds-step': MakePaymentFundsStep,
        'mdf-pay-funds-success-step': MdfPayFundsSuccessStep,
    },

    mixins: [MDFAgencyRequestMixin, MDFPayFundsMixin, FeeEnumMixin],

    provide() {
        return {
            accountId: this.widget.mdfRequest.agencyAccountId,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            paymentDetails: {
                selectedPaymentOption: 'payInGlasshive',
                baseFee: '',
                items: [],
                processingFee: '',
                serviceFee: '',
                transactionDate: '',
                paymentMethods: [],
                payMethod: {},
                agreement: false,
                companyName: '',
                invoiceNumber: '',
                amount: '',
                invoiceId: '',
                status: '',
                clientAccountId: undefined,
                clientHasBankAccount: false,
                paymentOptions: [
                    {
                        value: 'payInGlasshive',
                        title: 'Pay in Glasshive',
                    },
                    {
                        value: 'markAsPaid',
                        title: 'Mark as paid',
                        description: 'You will send the payment outside Glasshive',
                    },
                ],
            },

            isLoading: true,

            isPaying: false,

            currentStep: null,

            mdfRequestMainDetails: {},

            requestDetails: [],

            partnerName: null,

            tooltipOptions: {
                content: 'Funds Payer permission required',
            },

            steps: {
                payFundStep: {
                    title: 'Pay Funds',
                    hasCancelButton: true,
                    loading: false,
                    actionButton: () => {
                        this.$refs.payFundsStep.next();
                    },
                },

                makePaymentStep: {
                    title: 'Make a Payment',
                    hasCancelButton: true,
                    loading: false,
                    actionButton: () => {
                        this.isPaying = true;
                        this.$refs.makePaymentStep.next();
                    },
                },

                successStep: {
                    title: 'Success',
                    hasCancelButton: false,
                    loading: false,
                    actionButton: () => {
                        this.$refs.successStep.next();
                    },
                },
            },
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        mdfId() {
            return this.widget.mdfRequest.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        agencyAccountId() {
            return this.widget.mdfRequest.agencyAccountId;
        },

        invoiceId() {
            return this.widget.mdfRequest.invoiceId;
        },

        isMasterAgencyUser() {
            return this.$store.getters.isMasterAgencyUser;
        },

        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },

        isFundsPayer() {
            return (
                this.isMasterAgencyUser ||
                this.isOrganizationUser ||
                this.$store.getters.isBillingPaymentUser ||
                this.$store.state.user.roles.includes('FundsPayer')
            );
        },

        hasAgencyBillingPaymentPermission() {
            return (
                this.$store.getters.isBillingPaymentUser ||
                this.isOrganizationUser ||
                this.isMasterAgencyUser ||
                this.$store.getters.isAgencyAdmin
            );
        },

        invoiceTableDetails() {
            if (this.paymentDetails.selectedPaymentOption === 'payInGlasshive') {
                return Object.keys(this.invoiceDetails).map((key) => this.invoiceDetails[key]);
            }
            return [
                this.invoiceDetails.companySection,
                [this.invoiceDetails.feeSection[0]],
                this.invoiceDetails.amountSection,
            ];
        },

        invoiceDetails() {
            return this.getInvoiceDetails(this.paymentDetails);
        },
    },

    created() {
        EventBus.$on('payment:finished', this.handlePaymentFinished);
        this.goToStep('payFundStep');
        this.isLoading = true;
        this.initializeWidget()
            .then(this.getInvoiceData)
            .then(this.getCards)
            .then(this.getVerifyClientHasBankAccount)
            .finally(() => {
                this.isLoading = false;
            });
    },

    beforeDestroy() {
        EventBus.$off('payment:finished', this.handlePaymentFinished);
    },

    methods: {
        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        handlePaymentFinished() {
            this.isPaying = false;
        },

        getInvoiceData() {
            return MDFInvoicesService.getPaymentDetailsById(this.invoiceId, this.agencyAccountId)
                .then(({ data: { data } }) => {
                    this.paymentDetails = {
                        ...this.paymentDetails,
                        companyName: data.PayableTo,
                        amount: data.AmountDue,
                        invoiceId: data.InvoiceId,
                        clientAccountId: data.ClientAccountId,
                        status: data.Status,
                        baseFee: data.BaseFee,
                        items: data.Items,
                        processingFee: data.ProcessingFee,
                        serviceFee: data.ServiceFee,
                        transactionDate: data.TransactionDate,
                        isMarkedAsPaid:
                            data.Status === 'paid' && data.PaymentMethod === 'Marked as Paid',
                        isPaid: data.Status === 'paid',
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your information.',
                        { error },
                    );
                });
        },

        getInvoiceDetails(details) {
            return {
                companySection: [
                    {
                        title: 'Company Name',
                        content: details.companyName,
                    },
                    {
                        title: 'Invoice Number',
                        content: details.invoiceId,
                    },
                    {
                        title: details.items && details.items.length > 1 ? 'Items' : 'Item',
                        content: details.items
                            ? details.items.map((item) => item.FundTypeName).join(', ')
                            : '',
                    },
                    {
                        title: 'Transaction Date',
                        content: this.formatDate(details.transactionDate),
                        contentClass: 'uppercase',
                    },
                ],

                feeSection: [
                    {
                        title: 'Base Fee',
                        content: details.baseFee,
                        type: 'currency',
                    },
                    {
                        title: 'GlassHive Service Fee',
                        additionalTitleInfo: `(${this.feeEnum.mdfGlasshiveFee})`,
                        content: details.serviceFee,
                        type: 'currency',
                    },
                    {
                        title: 'Processing Fees',
                        additionalTitleInfo: `(${this.feeEnum.mdfProcessingFee})`,
                        content: details.processingFee,
                        type: 'currency',
                    },
                    ...(details.paymentMethod
                        ? [
                              {
                                  title: 'Payment Method',
                                  content: details.paymentMethod.paymentType,
                                  contentDetails: details.paymentMethod.cardDetails,
                                  contentClass: 'capitalize',
                              },
                          ]
                        : []),
                ],

                amountSection: [
                    {
                        title: this.currentStep === this.steps.successStep ? 'Total' : 'Amount Due',
                        content:
                            details.selectedPaymentOption === 'payInGlasshive'
                                ? details.amount
                                : details.baseFee,

                        rowClass: this.currentStep !== this.steps.successStep ? 'extra-bold' : '',
                        contentClass:
                            this.currentStep === this.steps.successStep ? 'total-amount' : '',

                        type: 'currency',
                    },
                ],
            };
        },

        getCards() {
            return new Promise((resolve, reject) => {
                PaymentMethodsService.getAllVerified(this.agencyId, 'agency')
                    .then(({ data }) => {
                        this.paymentDetails.paymentMethods = data.data
                            .map(this.mapItems)
                            .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
                        this.paymentDetails.payMethod = this.paymentDetails.paymentMethods[0];
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your information.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        mapItems(item) {
            if (item.PaymentType === 'card') {
                return {
                    type: item.Type,
                    cardType: item.CardType,
                    lastFourNumbers: item.CardLastFour,
                    isDefault: item.IsDefault,
                    key: `${item.CardLastFour}-${item.CardType}`,
                    id: item.Id,
                    imageCard: this.$helpers.setCardTypeData(item.CardType),
                    cardName: item.NickName,
                    expirationDate: '',
                };
            }
            return {
                accountType: item.BankAccountHolderType,
                lastFourNumbers: item.BankAccountLastFour,
                type: item.Type,
                id: item.Id,
                key: `${item.BankAccountLastFour}-${item.BankAccountHolderType}`,
                accountName: item.NickName,
                expirationDate: '',
                isDefault: item.IsDefault,
            };
        },

        initializeWidget() {
            return this.getWidgetRequestDetails(this.agencyAccountId, this.mdfId)
                .then(({ requestDetails, mdfRequestMainDetails }) => {
                    this.mdfRequestMainDetails = mdfRequestMainDetails;
                    this.requestDetails = requestDetails.filter(({ content }) => {
                        if (Array.isArray(content)) {
                            return content.length > 0;
                        }
                        return !!content;
                    });

                    this.partnerName = this.requestDetails.filter(
                        ({ label }) => label === 'Company Name',
                    )[0].content.name;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        "An error ocurred trying to get the request's details.",
                        { error },
                    );
                });
        },

        getVerifyClientHasBankAccount() {
            return new Promise((resolve, reject) => {
                MDFInvoicesService.getClientHasBankAccount({
                    invoiceId: this.paymentDetails.invoiceId,
                    clientAccountId: this.paymentDetails.clientAccountId,
                })
                    .then(({ data: { data } }) => {
                        this.paymentDetails.clientHasBankAccount = data;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your information.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        handleViewInvoice() {
            if (this.hasAgencyBillingPaymentPermission) {
                this.$router.push(
                    `${this.baseUrl}/Account/Billing?id=${this.invoiceId}&submenu=MDF#Invoices`,
                );
                this.closeWidget();
            } else {
                this.$helpers.showFailNotification('You need permission to redirect this page.');
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateDetails(details) {
            this.paymentDetails = {
                ...this.paymentDetails,
                ...details,
            };
        },

        updateIsLoading(isLoading) {
            this.isLoading = isLoading;
        },
    },
};
</script>

<style>
.mdf-pay-funds-widget .section-container {
    border-radius: 16px;
    border: 2px solid #e9e9ef;
    margin-top: 33px;
    padding: 29px 17px;
    position: relative;
}

.mdf-pay-funds-widget .section-container .section-title {
    background-color: #ffffff;
    left: 33%;
    position: absolute;
    text-align: center;
    top: -10px;
    width: 208px;
}

.mdf-pay-funds-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
    padding: 0 40px;
}

.mdf-pay-funds-widget .action-container .btn {
    padding: 0 40px;
}

.mdf-pay-funds-widget .action-container.make-payment {
    display: flex;
    gap: 16px;
}

.mdf-pay-funds-widget .action-container.make-payment .save-btn {
    background: transparent linear-gradient(0deg, #28da34 0%, #13e9e7 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px;
    width: 205px;
    height: 40px;
}

.mdf-pay-funds-widget .btn.secondary-btn {
    background: transparent;
    border: solid 1px #18aeff;
    color: #18aeff;
}

.mdf-pay-funds-widget .secondary-btn span.fa {
    margin-right: 10px;
}

.mdf-pay-funds-widget .cancel-container .btn.transparent-btn:hover,
.mdf-pay-funds-widget .btn.secondary-btn:hover {
    background: #e1e0e5;
}
</style>
